import { FC, useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import {
  ChangePasswordForm,
  ContentSectionLayout,
  FormikInputField,
  FormikToggleField,
  SafetyQuestionForm,
  toast,
  TwoColsLayout,
  types
} from "@vilocnv/allsetra-core";
import { HubConnectionState } from "@microsoft/signalr";
import { LockedIcon, MarkerIcon } from "assets/icons";
import { ChangePasswordContatiner } from "../UpdateSettingsForm.styled";
import { useAppDispatch, useAppSelector } from "hooks";
import { FormikHelpers, useFormikContext } from "formik";
import { getAllSafetyQuestionsThunk, resetPasswordThunk } from "app/features";
import { selectSettingsState } from "app/data/selectors";
import { useTranslation } from "react-i18next";
import { SignalRService } from "app/data/services";

const PASSWORD_RESET_TIMEOUT_MS = 10000;

type PasswordResetEvent = {
  eventName?: string;
  userId?: string;
  UserId?: string;
};

const isSignalRConnected = (): boolean =>
  SignalRService.hubConnection?.state === HubConnectionState.Connected;

// Fail closed: only accept events when both current user id and event user id are present and match.
const isPasswordResetEventForUser = (
  event: PasswordResetEvent,
  userId: string
): boolean => {
  if (event.eventName !== types.BackendEventsEnum.UserPasswordResetEvent) {
    return false;
  }

  if (!userId) {
    return false;
  }

  const eventUserId = event.userId ?? event.UserId;
  if (!eventUserId) {
    return false;
  }

  return eventUserId === userId;
};

const PersonalInfoForm: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const formik = useFormikContext();
  const { safetyQuestions, specificSetting } =
    useAppSelector(selectSettingsState);

  //Local State
  const [open, setOpen] = useState(false); // Boolean state for ChangePasswordForm Modal
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [openSafetyForm, setOpenSafetyForm] = useState(false);
  const passwordResetCleanupRef = useRef<(() => void) | null>(null);

  const { t } = useTranslation(["translation", "formFieldsTranslation"]);

  useEffect(() => {
    dispatch(getAllSafetyQuestionsThunk());
  }, []);

  useEffect(() => {
    return () => {
      passwordResetCleanupRef.current?.();
    };
  }, []);

  const handleClosePasswordModal = () => {
    passwordResetCleanupRef.current?.();
    setIsSubmittingPassword(false);
    setOpen(false);
  };

  const onFulfilledRequest = (
    formikHelpers: FormikHelpers<types.IChangePassword>
  ) => {
    handleClosePasswordModal();
    formikHelpers.setSubmitting(false);
    toast.success(
      t("errors.passwordChange.success", {
        defaultValue: "Your password has been updated successfully."
      })
    );
  };

  const onAcceptedWithoutConfirmation = (
    formikHelpers: FormikHelpers<types.IChangePassword>
  ) => {
    handleClosePasswordModal();
    formikHelpers.setSubmitting(false);
    toast.success(
      t("errors.passwordChange.accepted", {
        defaultValue:
          "Password change request accepted. Your password will be updated shortly."
      })
    );
  };

  const onFailedRequest = (
    formikHelpers: FormikHelpers<types.IChangePassword>
  ) => {
    setIsSubmittingPassword(false);
    formikHelpers.setSubmitting(false);
  };

  const handleChangePassword = async (
    values: types.IChangePassword,
    formikHelpers: FormikHelpers<types.IChangePassword>
  ) => {
    setIsSubmittingPassword(true);
    formikHelpers.setSubmitting(true);

    passwordResetCleanupRef.current?.();

    let eventReceived = false;
    let awaitingEvent = false;
    let earlyMatchingEvent: PasswordResetEvent | null = null;
    const currentUserId =
      (formik.values as { uniqueId?: string }).uniqueId ??
      (specificSetting as { uniqueId?: string } | null)?.uniqueId ??
      "";

    const timeoutRef: {
      id?: ReturnType<typeof setTimeout>;
    } = {};

    const completeOnEvent = () => {
      // Idempotent: handleEventRaised and the earlyMatchingEvent path can race.
      if (eventReceived) {
        return;
      }
      eventReceived = true;
      earlyMatchingEvent = null;
      clearTimeout(timeoutRef.id);
      SignalRService.hubConnection?.off("EventRaised", handleEventRaised);
      passwordResetCleanupRef.current = null;
      onFulfilledRequest(formikHelpers);
    };

    const handleEventRaised = (event: PasswordResetEvent) => {
      if (eventReceived) {
        return;
      }

      if (!isPasswordResetEventForUser(event, currentUserId)) {
        return;
      }

      // Capture events that arrive during the HTTP request so they are not lost
      // before awaitingEvent is flipped after a successful 202.
      if (!awaitingEvent) {
        earlyMatchingEvent = event;
        return;
      }

      completeOnEvent();
    };

    const cleanup = () => {
      clearTimeout(timeoutRef.id);
      SignalRService.hubConnection?.off("EventRaised", handleEventRaised);
      passwordResetCleanupRef.current = null;
    };

    // Bind (or re-bind) only when we can correlate events. Re-bind after HTTP so a
    // hub that connects mid-request still has a listener before we wait/timeout.
    const ensureListenerAttached = (): boolean => {
      if (!currentUserId || !isSignalRConnected()) {
        return false;
      }
      SignalRService.hubConnection?.off("EventRaised", handleEventRaised);
      SignalRService.hubConnection?.on("EventRaised", handleEventRaised);
      return true;
    };

    passwordResetCleanupRef.current = cleanup;

    // Best-effort early subscribe to buffer events that arrive during the HTTP call.
    ensureListenerAttached();

    const { type } = await dispatch(
      resetPasswordThunk({ password: values.password })
    );

    if (type !== "settings/resetPasswordThunk/fulfilled") {
      cleanup();
      onFailedRequest(formikHelpers);
      return;
    }

    // Missing uniqueId or disconnected hub: API accepted, but we cannot confirm via SignalR.
    if (!currentUserId || !isSignalRConnected()) {
      cleanup();
      onAcceptedWithoutConfirmation(formikHelpers);
      return;
    }

    // Connection may have become Connected only after the early subscribe attempt.
    if (!ensureListenerAttached()) {
      cleanup();
      onAcceptedWithoutConfirmation(formikHelpers);
      return;
    }

    awaitingEvent = true;

    if (earlyMatchingEvent) {
      completeOnEvent();
      return;
    }

    timeoutRef.id = setTimeout(() => {
      if (!eventReceived) {
        cleanup();
        onFailedRequest(formikHelpers);
        toast.error(
          t("errors.passwordChange.timeout", {
            defaultValue:
              "Password change confirmation timed out. Please try again or contact support if the issue persists."
          })
        );
      }
    }, PASSWORD_RESET_TIMEOUT_MS);
  };

  const handleChangeSafetyQuestion = async (
    values: types.ISafetyQuestion,
    formikHelpers: FormikHelpers<types.ISafetyQuestion>
  ) => {
    formikHelpers.setSubmitting(true);

    const { securityQuestionId, securityQuestionResponse } = values;

    const foundQuestion = safetyQuestions?.find(
      (question) => question.uniqueId === securityQuestionId
    );

    formik.setFieldValue("securityQuestionId", securityQuestionId);
    formik.setFieldValue("securityQuestionResponse", securityQuestionResponse);
    formik.setFieldValue("securityQuestion", foundQuestion.question);

    setOpenSafetyForm(false);

    formikHelpers.setSubmitting(false);
  };

  return (
    <Box>
      <ContentSectionLayout
        title={t("titles.personalInfo")}
        subTitle={t("descriptions.personalInfo")}
      >
        <Box>
          <TwoColsLayout>
            <FormikInputField
              theme={theme}
              label={t("userNumber.label", { ns: "formFieldsTranslation" })}
              name="uniqueId"
              fullWidth
              disabled
              endIcon={<LockedIcon />}
            />
            <FormikInputField
              theme={theme}
              label={t("currentRole.label", { ns: "formFieldsTranslation" })}
              name="role"
              fullWidth
              disabled
              endIcon={<LockedIcon />}
            />
          </TwoColsLayout>

          <TwoColsLayout>
            <FormikInputField
              theme={theme}
              label={t("firstName.label", { ns: "formFieldsTranslation" })}
              name="firstName"
              fullWidth
            />
            <FormikInputField
              theme={theme}
              label={t("lastName.label", { ns: "formFieldsTranslation" })}
              name="lastName"
              fullWidth
            />
          </TwoColsLayout>

          <TwoColsLayout hideDivider>
            <FormikInputField
              theme={theme}
              label={t("email.label", { ns: "formFieldsTranslation" })}
              name="email"
              fullWidth
              disabled
              endIcon={<LockedIcon />}
            />
            <FormikInputField
              theme={theme}
              label={t("phoneNumber.label", { ns: "formFieldsTranslation" })}
              name="phone"
              fullWidth
            />
          </TwoColsLayout>
          <Box mt={2}>
            <FormikToggleField
              id="settings-email-notification"
              label={t("emailNotifications.label", {
                ns: "formFieldsTranslation"
              })}
              name="isEmailNotificationEnabled"
            />
          </Box>

          <TwoColsLayout>
            <ChangePasswordContatiner
              id="settings-change-password"
              onClick={() => setOpen(true)}
            >
              <MarkerIcon />
              <Box>
                {t("changePassword.label", {
                  ns: "formFieldsTranslation"
                })}
              </Box>
            </ChangePasswordContatiner>
          </TwoColsLayout>

          <TwoColsLayout hideDivider>
            <FormikInputField
              theme={theme}
              label={t("securityQuestion.label", {
                ns: "formFieldsTranslation"
              })}
              name="securityQuestion"
              fullWidth
              disabled
            />
            <FormikInputField
              theme={theme}
              label={t("securityQuestionResponse.label", {
                ns: "formFieldsTranslation"
              })}
              name="securityQuestionResponse"
              fullWidth
              disabled
            />
          </TwoColsLayout>
          <TwoColsLayout hideDivider>
            <ChangePasswordContatiner
              id="settings-change-safety-qs"
              onClick={() => setOpenSafetyForm(true)}
            >
              <MarkerIcon />
              <Box>
                {t("changeSafetyQuestion.label", {
                  ns: "formFieldsTranslation"
                })}
              </Box>
            </ChangePasswordContatiner>
          </TwoColsLayout>
        </Box>
      </ContentSectionLayout>
      <ChangePasswordForm
        open={open}
        onClose={handleClosePasswordModal}
        theme={theme}
        onSubmit={handleChangePassword}
        submitting={isSubmittingPassword}
        translator={t}
      />
      <SafetyQuestionForm
        open={openSafetyForm}
        onClose={() => setOpenSafetyForm(false)}
        theme={theme}
        onSubmit={handleChangeSafetyQuestion}
        safetyQuestions={safetyQuestions}
        translator={t}
      />
    </Box>
  );
};

export default PersonalInfoForm;
